import { useMemo, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import { SectionTag } from "./About";

const IMAGES = {
  web: "https://images.unsplash.com/photo-1759661881353-5b9cc55e1cf4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  systems:
    "https://images.unsplash.com/photo-1680992044138-ce4864c2b962?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  network:
    "https://images.unsplash.com/photo-1691435828932-911a7801adfb?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  abstract:
    "https://images.unsplash.com/photo-1638356554489-decb2e3a4c37?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  server:
    "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
};

const IMAGE_BY_INDEX = [
  IMAGES.web,
  IMAGES.systems,
  IMAGES.network,
  IMAGES.abstract,
  IMAGES.web,
  IMAGES.server,
];

export default function Portfolio() {
  const { t } = useLang();
  const [filter, setFilter] = useState("all");

  const items = useMemo(() => {
    return t.portfolio.items.map((it, i) => ({
      ...it,
      img: IMAGE_BY_INDEX[i % IMAGE_BY_INDEX.length],
    }));
  }, [t]);

  const filtered = filter === "all" ? items : items.filter((i) => i.cat === filter);
  const filterKeys = ["all", "web", "systems", "network"];

  return (
    <section
      id="portfolio"
      className="relative py-24 sm:py-32 border-t border-white/5"
      data-testid="portfolio-section"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <SectionTag>{t.portfolio.tag}</SectionTag>
            <h2
              className="font-display mt-5 text-3xl sm:text-4xl lg:text-5xl text-white font-bold leading-[1.1]"
              data-testid="portfolio-title"
            >
              {t.portfolio.title}
            </h2>
            <p className="mt-5 text-slate-200 text-base sm:text-lg leading-[1.75]">
              {t.portfolio.sub}
            </p>
          </div>

          <div
            className="flex flex-wrap gap-2"
            data-testid="portfolio-filters"
          >
            {filterKeys.map((k) => (
              <button
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`portfolio-filter-${k}`}
                className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-mono-tech border transition-colors ${
                  filter === k
                    ? "bg-[#00F0FF] text-[#050B14] border-[#00F0FF]"
                    : "text-slate-300 border-white/15 hover:border-[#00F0FF] hover:text-[#00F0FF]"
                }`}
              >
                {t.portfolio.filters[k]}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((it, i) => (
            <Link
              key={i}
              to="/contact"
              data-testid={`portfolio-item-${i}`}
              className="group relative overflow-hidden border border-[rgba(0,240,255,0.15)] hover:border-[rgba(0,240,255,0.5)] transition-all"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-[#0A1428]">
                <img
                  src={it.img}
                  alt={it.name}
                  className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-500"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/40 to-transparent" />
                <span className="absolute top-3 start-3 font-mono-tech text-[10px] uppercase tracking-[0.24em] text-[#00F0FF] bg-[#050B14]/70 border border-[#00F0FF]/30 px-2 py-1">
                  {it.tag}
                </span>
              </div>
              <div className="p-5 flex items-center justify-between bg-[#050B14]">
                <h3 className="font-display text-lg text-white font-semibold">
                  {it.name}
                </h3>
                <ArrowUpRight
                  size={18}
                  className="text-slate-500 group-hover:text-[#00F0FF] transition-all group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
